<template>
	<v-menu
		offset-y
		:close-on-content-click="closeOnContentClick"
		:value="value || menuOpen"
		@input="onMenuInput"
		:nudge-bottom="8"
	>
		<template v-slot:activator="{ on, attrs, value: menuValue }">
			<slot
				name="activator"
				:on="on"
				:attrs="attrs"
				:menuValue="menuValue"
			>
				<!-- Default button when no activator slot is provided -->
				<v-btn
					v-bind="attrs"
					v-on="on"
					:color="color"
					:disabled="disabled"
					:class="buttonClass"
				>
					<v-icon class="mr-1">{{ icon }}</v-icon>
					{{ buttonText }}
					<v-icon v-if="menuValue">mdi-menu-up</v-icon>
					<v-icon v-else>mdi-menu-down</v-icon>
				</v-btn>
			</slot>
		</template>

		<v-card min-width="300" class="ma-0">
			<v-list>
				<!-- Direct Download Checkbox (moved to top) -->
				<v-list-item
					v-if="showExecutionOptions"
					class="execution-option-item py-2"
				>
					<div class="d-flex align-center w-100">
						<v-checkbox
							:input-value="!isBackgroundExport"
							@change="onDirectDownloadChange"
							color="primary"
							class="execution-checkbox"
							hide-details
							dense
						></v-checkbox
						><span
							class="execution-option-title ml-1"
							@click.stop="
								onDirectDownloadChange(!isBackgroundExport)
							"
						>
							<v-icon small class="mr-1">mdi-download</v-icon>
							Direct Download
						</span>
						<v-tooltip bottom class="ml-3">
							<template v-slot:activator="{ on, attrs }">
								<v-icon
									v-bind="attrs"
									v-on="on"
									small
									color="grey"
									class="ml-2"
								>
									mdi-information-outline
								</v-icon>
							</template>
							<span
								>Uncheck this to receive a notification when the
								file is ready for download</span
							>
						</v-tooltip>
					</div>
				</v-list-item>
				<!-- Time Zone Selection -->
				<v-list-item v-if="showTimeZoneOptions">
					<v-list-item-content class="pa-0">
						<v-btn-toggle
							:value="isUtcTime"
							@change="onTimeZoneChange"
							mandatory
							dense
							class="time-format-toggle w-100"
							style="display: flex; width: 100%"
						>
							<v-btn :value="false" small class="flex-grow-1">
								<v-icon small left>mdi-clock-outline</v-icon>
								Local Time
							</v-btn>
							<v-btn :value="true" small class="flex-grow-1">
								<v-icon small left>mdi-earth</v-icon>
								UTC
							</v-btn>
						</v-btn-toggle>
					</v-list-item-content>
				</v-list-item>

				<v-divider
					v-if="showTimeZoneOptions || showExecutionOptions"
				></v-divider>

				<!-- Data Scope Options -->
				<template v-if="showDataScopeOptions">
					<!-- Visible Data Option -->
					<v-list-item
						v-if="showVisibleDataOption"
						@click="onExportVisibleData"
					>
						<v-list-item-content>
							<v-list-item-title>
								<v-icon small left>mdi-eye</v-icon>
								Visible data
							</v-list-item-title>
						</v-list-item-content>
					</v-list-item>

					<!-- All Data Option -->
					<v-list-item
						v-if="showAllDataOption"
						@click="onExportAllData"
					>
						<v-list-item-content>
							<v-list-item-title>
								<v-icon small left>mdi-database</v-icon>
								All data
							</v-list-item-title>
						</v-list-item-content>
					</v-list-item>

					<v-divider
						v-if="
							(showVisibleDataOption || showAllDataOption) &&
							hasSlotContent
						"
					></v-divider>
				</template>
				<!-- Custom Content Slot (for DataExports specific features) -->
				<slot
					name="custom-options"
					:timeZone="timeZone"
					:isBackgroundExport="isBackgroundExport"
					:exportConfig="exportConfig"
				></slot>
			</v-list>
		</v-card>
	</v-menu>
</template>

<script>
export default {
	name: 'ExportModal',
	props: {
		// Menu control
		value: {
			type: Boolean,
			default: false,
		},
		closeOnContentClick: {
			type: Boolean,
			default: false,
		},

		// Button appearance
		buttonText: {
			type: String,
			default: 'Export',
		},
		buttonClass: {
			type: String,
			default: '',
		},
		color: {
			type: String,
			default: 'primary',
		},
		icon: {
			type: String,
			default: 'mdi-cloud-download',
		},
		disabled: {
			type: Boolean,
			default: false,
		},

		// Feature toggles
		showTimeZoneOptions: {
			type: Boolean,
			default: true,
		},
		showExecutionOptions: {
			type: Boolean,
			default: true,
		},
		showDataScopeOptions: {
			type: Boolean,
			default: true,
		},
		showVisibleDataOption: {
			type: Boolean,
			default: true,
		},
		showAllDataOption: {
			type: Boolean,
			default: true,
		},

		// Default values
		defaultUtcTime: {
			type: Boolean,
			default: false,
		},
		defaultBackgroundExport: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			isUtcTime: this.defaultUtcTime,
			isBackgroundExport: this.defaultBackgroundExport,
			menuOpen: false,
		};
	},
	computed: {
		timeZone() {
			return this.isUtcTime ? 0 : new Date().getTimezoneOffset() * -1;
		},
		exportConfig() {
			return {
				timeZone: this.timeZone,
				isBackgroundExport: this.isBackgroundExport,
			};
		},
		hasSlotContent() {
			return (
				!!this.$slots['custom-options'] ||
				!!this.$scopedSlots['custom-options']
			);
		},
	},
	methods: {
		onMenuInput(value) {
			this.menuOpen = value;
			this.$emit('input', value);
		},
		onTimeZoneChange(value) {
			this.isUtcTime = value;
		},
		onExecutionModeChange(value) {
			this.isBackgroundExport = value;
		},
		onDirectDownloadChange(value) {
			// Handle both checkbox change events and direct calls with parameters
			if (typeof value === 'boolean') {
				this.isBackgroundExport = !value;
			} else {
				// This is a checkbox change event, value is the new checkbox state
				this.isBackgroundExport = !value;
			}
		},
		onExportVisibleData() {
			const exportData = {
				timeZone: this.timeZone,
				isBackgroundExport: this.isBackgroundExport,
				allData: false,
			};
			this.$emit('export', exportData);
			this.$emit('exportModalEvent', exportData);
			// Close the modal when an export option is selected
			this.menuOpen = false;
			this.$emit('input', false);
		},
		onExportAllData() {
			const exportData = {
				timeZone: this.timeZone,
				isBackgroundExport: this.isBackgroundExport,
				allData: true,
			};
			this.$emit('export', exportData);
			this.$emit('exportModalEvent', exportData);
			// Close the modal when an export option is selected
			this.menuOpen = false;
			this.$emit('input', false);
		},
	},
};
</script>

<style scoped>
.time-format-toggle .v-btn {
	border-radius: 4px !important;
}

.execution-mode-toggle .v-btn {
	border-radius: 4px !important;
}

.v-btn-toggle {
	height: 36px;
}

.v-list-item-content {
	padding: 8px 0;
}

.execution-option-item {
	min-height: auto !important;
}

.execution-option-item .v-list-item-content {
	padding: 0 !important;
}

.execution-checkbox {
	margin: 0 !important;
	padding: 0 !important;
	height: auto !important;
	flex: 0 0 auto;
}

.execution-checkbox .v-input {
	margin: 0 !important;
	padding: 0 !important;
}

.execution-checkbox .v-input__control {
	margin: 0 !important;
	padding: 0 !important;
	min-height: auto !important;
}

.execution-checkbox .v-input--selection-controls__input {
	margin: 0 !important;
	height: 20px;
	width: 20px;
}

.execution-checkbox .v-input__slot {
	margin: 0 !important;
	padding: 0 !important;
	align-items: center;
}

.execution-option-title {
	font-size: 14px;
	font-weight: normal;
	user-select: none;
	cursor: pointer;
	line-height: 20px;
	display: inline-block;
}
</style>
